/**
 * Resolve the document reference a skill hands the add-in (an Actions-tab
 * card's Open Document button, or a Navigate-to-Knowledge record) into the
 * drive item it names.
 *
 * Three reference shapes are accepted, because a skill can only pass along
 * what its upstream tools produced:
 *   - a SharePoint / OneDrive web URL  → Graph `/shares/{encoded}/driveItem`
 *   - `drives/{driveId}/items/{itemId}` → that item
 *   - a bare item id                    → the user's own OneDrive
 * Anything else is rejected rather than spliced into a Graph path.
 */
import { bytesToBase64 } from "../../../shared/base64";
import type { RefreshTokenFn } from "../../../shared/api/fetchWithRefresh";
import { graphGetJson } from "../../email/services/graph";

export interface ResolvedDriveItem {
  readonly id: string;
  readonly name: string;
  readonly webUrl: string;
  /** Null when Graph omits `parentReference` — the item cannot be previewed by drive then. */
  readonly driveId: string | null;
  readonly isFolder: boolean;
}

export class InvalidDriveItemRefError extends Error {
  constructor() {
    super("Unrecognised document reference");
    this.name = "InvalidDriveItemRefError";
  }
}

const DRIVE_ITEM_PATH = /^\/?drives\/([^/?#]+)\/items\/([^/?#]+)$/i;
const BARE_ITEM_ID = /^[^/?#\s]+$/;

export function isWebUrl(ref: string): boolean {
  return /^https?:\/\//i.test(ref.trim());
}

/**
 * Turn a SharePoint/OneDrive item webUrl into a "view in the browser" URL.
 * SharePoint & OneDrive honour `?web=1`, which renders the file in Office
 * Online / the built-in PDF viewer instead of streaming it as a download — so
 * clicking a file opens it in a new tab rather than downloading it (or being
 * blocked by a download-scanning proxy). Falls back to the raw URL if it can't
 * be parsed.
 */
export function toBrowserViewUrl(webUrl: string): string {
  try {
    const url = new URL(webUrl);
    url.searchParams.set("web", "1");
    return url.toString();
  } catch {
    return webUrl;
  }
}

/** Graph's sharing-URL encoding: `u!` + unpadded base64url of the UTF-8 URL. */
function encodeSharingUrl(url: string): string {
  const base64 = bytesToBase64(new TextEncoder().encode(url));
  // Padding is at most two `=`; trimmed by scan rather than a `=+$` regex, which backtracks.
  let end = base64.length;
  while (end > 0 && base64[end - 1] === "=") end--;
  return `u!${base64.slice(0, end).replace(/\//g, "_").replace(/\+/g, "-")}`;
}

/** The Graph path for `ref`; throws InvalidDriveItemRefError for an unrecognised shape. */
export function driveItemGraphPath(ref: string): string {
  const trimmed = ref.trim();
  if (isWebUrl(trimmed)) return `/shares/${encodeSharingUrl(trimmed)}/driveItem`;
  const drivePath = DRIVE_ITEM_PATH.exec(trimmed);
  if (drivePath?.[1] && drivePath[2]) {
    return `/drives/${encodeURIComponent(drivePath[1])}/items/${encodeURIComponent(drivePath[2])}`;
  }
  if (BARE_ITEM_ID.test(trimmed)) return `/me/drive/items/${encodeURIComponent(trimmed)}`;
  throw new InvalidDriveItemRefError();
}

interface GraphDriveItemResponse {
  id?: unknown;
  name?: unknown;
  webUrl?: unknown;
  folder?: unknown;
  parentReference?: { driveId?: unknown } | null;
}

/** Fetch the item `ref` names. Throws GraphApiError on a non-2xx, InvalidDriveItemRefError on a bad ref or body. */
export async function resolveDriveItem(
  token: string,
  ref: string,
  refresh?: RefreshTokenFn,
): Promise<ResolvedDriveItem> {
  const path = `${driveItemGraphPath(ref)}?$select=id,name,webUrl,folder,parentReference`;
  const item = await graphGetJson<GraphDriveItemResponse>(path, token, refresh);
  if (typeof item?.id !== "string" || typeof item.webUrl !== "string") {
    throw new InvalidDriveItemRefError();
  }
  const driveId = item.parentReference?.driveId;
  return {
    id: item.id,
    name: typeof item.name === "string" ? item.name : "",
    webUrl: item.webUrl,
    driveId: typeof driveId === "string" ? driveId : null,
    isFolder: item.folder != null,
  };
}
